import moment from 'moment-timezone';

function normalizeTime(value: string): string {
  const parts = String(value).split(':');
  if (parts.length >= 2) {
    return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
  }
  return String(value);
}

function timeToMinutes(value: string): number {
  const [h, m] = normalizeTime(value).split(':').map(Number);
  if ([h, m].some((n) => Number.isNaN(n))) return 0;
  return h * 60 + m;
}

export interface ScheduleLike {
  date?: string;
  startTime?: string;
  endTime?: string;
  startTimeUTC?: string;
  endTimeUTC?: string;
}

function scheduleToInterval(schedule: ScheduleLike, timezone: string): { start: number; end: number } | null {
  if (
    schedule.startTimeUTC
    && schedule.endTimeUTC
    && schedule.startTimeUTC.includes('T')
    && schedule.startTimeUTC.includes('Z')
  ) {
    const start = moment.utc(schedule.startTimeUTC);
    const end = moment.utc(schedule.endTimeUTC);
    return { start: start.valueOf(), end: end.valueOf() };
  }

  if (!schedule.startTime || !schedule.endTime) return null;

  const scheduleDate = schedule.date || moment().tz(timezone).format('YYYY-MM-DD');
  const start = moment.tz(`${scheduleDate}T${schedule.startTime}:00`, 'YYYY-MM-DDTHH:mm:ss', timezone);
  const end = moment.tz(`${scheduleDate}T${schedule.endTime}:00`, 'YYYY-MM-DDTHH:mm:ss', timezone);
  return { start: start.valueOf(), end: end.valueOf() };
}

/** Union of overlapping schedule windows in hours (no double-count for shared coverage). */
export function mergeScheduleHours(schedules: ScheduleLike[], timezone: string): number {
  if (!schedules.length) return 0;

  const ranges = schedules
    .map((schedule) => scheduleToInterval(schedule, timezone))
    .filter((range): range is { start: number; end: number } => Boolean(range && range.end > range.start))
    .sort((a, b) => a.start - b.start);

  if (!ranges.length) return 0;

  let totalMs = 0;
  let currentStart = ranges[0].start;
  let currentEnd = ranges[0].end;

  for (let i = 1; i < ranges.length; i += 1) {
    if (ranges[i].start <= currentEnd) {
      currentEnd = Math.max(currentEnd, ranges[i].end);
    } else {
      totalMs += currentEnd - currentStart;
      currentStart = ranges[i].start;
      currentEnd = ranges[i].end;
    }
  }

  totalMs += currentEnd - currentStart;
  return parseFloat((totalMs / 3600000).toFixed(1));
}
